/**
 * Error Boundary Component
 * Catches JavaScript errors in child components and displays a fallback UI
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { logger } from '../lib/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    // Route through `logger.error` so the backend client-errors
    // collection captures the crash too. In dev this still prints to
    // console because `logger.error` no-ops to console there.
    logger.error('ErrorBoundary caught an error:', error, errorInfo?.componentStack || '');
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Optionally reload the page
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-700" />
            </div>
            <h2 className="font-display text-xl font-bold text-heda-ink mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-heda-mute mb-4">
              {this.props.fallbackMessage || "We're sorry, but something unexpected happened. Please try again."}
            </p>
            <div className="flex gap-2 justify-center">
              <Button
                onClick={this.handleReset}
                variant="outline"
                className="rounded-xl text-sm bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Try Again
              </Button>
              <Button
                onClick={() => window.location.reload()}
                className="rounded-xl bg-heda-ink hover:bg-black text-sm"
              >
                Reload Page
              </Button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left bg-heda-cream rounded-lg p-3 text-xs">
                <summary className="cursor-pointer text-red-700 font-medium">
                  Error Details (Dev Only)
                </summary>
                <pre className="mt-2 overflow-auto text-heda-ink">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
